/**
 * PublicAPI Page
 *
 * Documentation and management page for the Public API v1.
 * Displays endpoints, authentication info, rate limits, and code examples.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../components/Icons';
import { useDarkMode } from '../hooks/useDarkMode';
import { useToast } from '../components/Toast';

// API Version and base URL
const API_VERSION = 'v1';
const API_BASE_URL = 'https://app.edouard.ai/api/v1';

// Endpoint translation keys - categories and descriptions
const getEndpointsConfig = (t: (key: string, fallback: string) => string) => [
  {
    category: t('publicApi.categories.general', 'General'),
    icon: 'Activity',
    endpoints: [
      {
        method: 'GET',
        path: '/status',
        description: t('publicApi.endpoints.status.desc', 'API status and available endpoints'),
        auth: false,
        params: []
      }
    ]
  },
  {
    category: t('publicApi.categories.portfolio', 'Portfolio'),
    icon: 'Wallet',
    endpoints: [
      {
        method: 'GET',
        path: '/portfolio',
        description: t('publicApi.endpoints.portfolio.desc', 'Portfolio summary with tokens and positions'),
        auth: true,
        feature: 'portfolio_read',
        params: [
          { name: 'portfolio_id', type: 'string', required: false, description: t('publicApi.params.portfolioId', 'Specific portfolio ID') },
          { name: 'include_positions', type: 'boolean', default: 'false', description: t('publicApi.params.includePositions', 'Include detailed positions') },
          { name: 'currency', type: 'string', default: 'usd', description: t('publicApi.params.currency', 'Currency (usd, eur, btc)') }
        ]
      },
      {
        method: 'GET',
        path: '/portfolio/snapshots',
        description: t('publicApi.endpoints.portfolioSnapshots.desc', 'Portfolio snapshots history'),
        auth: true,
        feature: 'portfolio_read',
        params: [
          { name: 'portfolio_id', type: 'string', required: false, description: t('publicApi.params.portfolioId', 'Portfolio ID') },
          { name: 'from', type: 'string', required: false, description: t('publicApi.params.fromDate', 'Start date (ISO 8601)') },
          { name: 'to', type: 'string', required: false, description: t('publicApi.params.toDate', 'End date (ISO 8601)') },
          { name: 'limit', type: 'number', default: '30', description: t('publicApi.params.snapshotLimit', 'Number of snapshots (max 365)') }
        ]
      }
    ]
  },
  {
    category: t('publicApi.categories.widgets', 'iOS / Widgets'),
    icon: 'Smartphone',
    endpoints: [
      {
        method: 'GET',
        path: '/widget/summary',
        description: t('publicApi.endpoints.widgetSummary.desc', 'Optimized data for iOS widgets (portfolio + nodes)'),
        auth: true,
        feature: 'widgets',
        params: [
          { name: 'include_flux', type: 'boolean', default: 'true', description: t('publicApi.params.includeFlux', 'Include FluxNodes') },
          { name: 'include_cronos', type: 'boolean', default: 'true', description: t('publicApi.params.includeCronos', 'Include Cronos staking') },
          { name: 'include_hedera', type: 'boolean', default: 'true', description: t('publicApi.params.includeHedera', 'Include Hedera accounts') }
        ]
      },
      {
        method: 'GET',
        path: '/monitor/wallets',
        description: t('publicApi.endpoints.monitorWalletsList.desc', 'List of monitored wallets'),
        auth: true,
        feature: 'monitor',
        params: [
          { name: 'type', type: 'string', required: false, description: t('publicApi.params.walletType', 'Wallet type (flux, cronos, hedera, thegraph)') }
        ]
      },
      {
        method: 'POST',
        path: '/monitor/wallets',
        description: t('publicApi.endpoints.monitorWalletsAdd.desc', 'Add a wallet to monitor'),
        auth: true,
        feature: 'monitor',
        params: [
          { name: 'address', type: 'string', required: true, description: t('publicApi.params.walletAddress', 'Wallet address') },
          { name: 'type', type: 'string', required: true, description: t('publicApi.params.walletTypeRequired', 'Type (flux, cronos, hedera, thegraph)') },
          { name: 'label', type: 'string', required: false, description: t('publicApi.params.walletLabel', 'Custom name') }
        ]
      },
      {
        method: 'DELETE',
        path: '/monitor/wallets/:id',
        description: t('publicApi.endpoints.monitorWalletsDelete.desc', 'Remove a monitored wallet'),
        auth: true,
        feature: 'monitor',
        params: [
          { name: 'id', type: 'string', required: true, description: t('publicApi.params.walletId', 'Wallet ID') }
        ]
      }
    ]
  },
  {
    category: t('publicApi.categories.alerts', 'Alerts'),
    icon: 'Bell',
    endpoints: [
      {
        method: 'GET',
        path: '/alerts',
        description: t('publicApi.endpoints.alertsList.desc', 'List of configured price alerts'),
        auth: true,
        feature: 'alerts',
        params: [
          { name: 'status', type: 'string', default: 'active', description: t('publicApi.params.alertStatus', 'Filter by status (active, triggered, all)') }
        ]
      },
      {
        method: 'POST',
        path: '/alerts',
        description: t('publicApi.endpoints.alertsCreate.desc', 'Create a price alert'),
        auth: true,
        feature: 'alerts',
        params: [
          { name: 'token_id', type: 'string', required: true, description: t('publicApi.params.tokenId', 'CoinGecko token ID') },
          { name: 'condition', type: 'string', required: true, description: t('publicApi.params.alertCondition', 'above or below') },
          { name: 'target_price', type: 'number', required: true, description: t('publicApi.params.targetPrice', 'Target price in USD') },
          { name: 'repeatable', type: 'boolean', default: 'false', description: t('publicApi.params.repeatable', 'Repeatable alert') }
        ]
      },
      {
        method: 'DELETE',
        path: '/alerts/:id',
        description: t('publicApi.endpoints.alertsDelete.desc', 'Delete an alert'),
        auth: true,
        feature: 'alerts',
        params: [
          { name: 'id', type: 'string', required: true, description: t('publicApi.params.alertId', 'Alert ID') }
        ]
      }
    ]
  },
  {
    category: t('publicApi.categories.prices', 'Prices'),
    icon: 'TrendingUp',
    endpoints: [
      {
        method: 'GET',
        path: '/prices/history',
        description: t('publicApi.endpoints.pricesHistory.desc', 'Token price history'),
        auth: true,
        feature: 'prices',
        params: [
          { name: 'token_id', type: 'string', required: true, description: t('publicApi.params.tokenId', 'CoinGecko token ID') },
          { name: 'days', type: 'number', default: '30', description: t('publicApi.params.days', 'Number of days (1-365)') },
          { name: 'currency', type: 'string', default: 'usd', description: t('publicApi.params.currency', 'Currency (usd, eur, btc)') }
        ]
      }
    ]
  },
  {
    category: t('publicApi.categories.taxes', 'Taxes'),
    icon: 'Calculator',
    endpoints: [
      {
        method: 'GET',
        path: '/tax/summary',
        description: t('publicApi.endpoints.taxSummary.desc', 'Tax calculation (Article 150 VH bis CGI)'),
        auth: true,
        feature: 'tax',
        params: [
          { name: 'year', type: 'number', default: t('publicApi.params.currentYear', 'Current year'), description: t('publicApi.params.fiscalYear', 'Fiscal year (2019-2024)') },
          { name: 'include_transactions', type: 'boolean', default: 'false', description: t('publicApi.params.includeTransactions', 'Include transaction details') },
          { name: 'portfolio_id', type: 'string', required: false, description: t('publicApi.params.limitToPortfolio', 'Limit to a portfolio') }
        ]
      }
    ]
  },
  {
    category: t('publicApi.categories.nodes', 'Nodes'),
    icon: 'Server',
    endpoints: [
      {
        method: 'GET',
        path: '/nodes/flux',
        description: t('publicApi.endpoints.nodesFlux.desc', 'FluxNodes status'),
        auth: true,
        feature: 'nodes',
        params: [
          { name: 'wallets', type: 'string', required: true, description: t('publicApi.params.fluxWallets', 'Flux addresses (t1xxx, t3xxx) comma-separated') },
          { name: 'include_rewards', type: 'boolean', default: 'false', description: t('publicApi.params.includeRewards', 'Include rewards history') },
          { name: 'include_network', type: 'boolean', default: 'true', description: t('publicApi.params.includeNetwork', 'Include network stats') }
        ]
      },
      {
        method: 'GET',
        path: '/nodes/flux/rewards',
        description: t('publicApi.endpoints.nodesFluxRewards.desc', 'FluxNode rewards history'),
        auth: true,
        feature: 'nodes',
        params: [
          { name: 'wallet', type: 'string', required: true, description: t('publicApi.params.fluxAddress', 'Flux address') },
          { name: 'days', type: 'number', default: '30', description: t('publicApi.params.daysMax365', 'Number of days (max 365)') }
        ]
      },
      {
        method: 'GET',
        path: '/nodes/cronos',
        description: t('publicApi.endpoints.nodesCronos.desc', 'Cronos POS staking data'),
        auth: true,
        feature: 'nodes',
        params: [
          { name: 'wallets', type: 'string', required: true, description: t('publicApi.params.cronosWallets', 'Cronos addresses (cro1xxx) comma-separated') },
          { name: 'include_validators', type: 'boolean', default: 'true', description: t('publicApi.params.includeValidators', 'Include validator details') },
          { name: 'include_unbonding', type: 'boolean', default: 'true', description: t('publicApi.params.includeUnbonding', 'Include unbondings') }
        ]
      },
      {
        method: 'GET',
        path: '/nodes/hedera',
        description: t('publicApi.endpoints.nodesHedera.desc', 'Hedera account data (HBAR)'),
        auth: true,
        feature: 'nodes',
        params: [
          { name: 'accounts', type: 'string', required: true, description: t('publicApi.params.hederaAccounts', 'Account IDs (0.0.xxxxx) comma-separated') },
          { name: 'include_tokens', type: 'boolean', default: 'true', description: t('publicApi.params.includeTokens', 'Include HTS tokens') },
          { name: 'include_nfts', type: 'boolean', default: 'false', description: t('publicApi.params.includeNfts', 'Include NFTs') },
          { name: 'include_transactions', type: 'boolean', default: 'false', description: t('publicApi.params.includeRecentTx', 'Include recent transactions') }
        ]
      }
    ]
  },
  {
    category: t('publicApi.categories.hosting', 'Hosting'),
    icon: 'Cloud',
    endpoints: [
      {
        method: 'GET',
        path: '/hosting/subscriptions',
        description: t('publicApi.endpoints.hostingList.desc', 'FluxNode hosting subscriptions list'),
        auth: true,
        feature: 'hosting',
        params: [
          { name: 'status', type: 'string', default: 'active', description: t('publicApi.params.hostingStatus', 'Filter by status (active, pending, expired)') }
        ]
      },
      {
        method: 'POST',
        path: '/hosting/subscriptions',
        description: t('publicApi.endpoints.hostingCreate.desc', 'Create a hosting request'),
        auth: true,
        feature: 'hosting',
        params: [
          { name: 'tier', type: 'string', required: true, description: t('publicApi.params.nodeTier', 'Tier (cumulus, nimbus, stratus)') },
          { name: 'wallet', type: 'string', required: true, description: t('publicApi.params.nodeWallet', 'Flux node address') },
          { name: 'months', type: 'number', default: '1', description: t('publicApi.params.duration', 'Duration (1-12 months)') }
        ]
      }
    ]
  }
];

// Code examples
const CODE_EXAMPLES = {
  curl: `curl -X GET \\
  -H "Authorization: Bearer sk_live_xxxxx" \\
  "${API_BASE_URL}/portfolio?include_positions=true"`,

  javascript: `const API_KEY = 'sk_live_xxxxx';
const BASE_URL = '${API_BASE_URL}';

async function getPortfolio() {
  const response = await fetch(\`\${BASE_URL}/portfolio\`, {
    headers: {
      'Authorization': \`Bearer \${API_KEY}\`
    }
  });

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error.message);
  }

  return data.data;
}`,

  python: `import requests

API_KEY = 'sk_live_xxxxx'
BASE_URL = '${API_BASE_URL}'

def get_tax_summary(year):
    response = requests.get(
        f'{BASE_URL}/tax/summary',
        params={'year': year},
        headers={'Authorization': f'Bearer {API_KEY}'}
    )

    data = response.json()

    if not data['success']:
        raise Exception(data['error']['message'])

    return data['data']

# Usage
summary = get_tax_summary(2024)
print(f"Plus-value nette: {summary['summary']['netCapitalGain']}€")`
};

type TabType = 'overview' | 'endpoints' | 'authentication' | 'examples' | 'keys';
type CodeLang = 'curl' | 'javascript' | 'python';

interface PublicAPIProps {
  onNavigateToSettings?: () => void;
}

export function PublicAPI({ onNavigateToSettings }: PublicAPIProps) {
  const { t } = useTranslation();
  const [isDark] = useDarkMode();
  const toast = useToast();
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [selectedLang, setSelectedLang] = useState<CodeLang>('javascript');
  const [expandedEndpoint, setExpandedEndpoint] = useState<string | null>(null);

  // Get translated endpoints configuration
  const ENDPOINTS = getEndpointsConfig(t);

  // Copy to clipboard
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast(t('publicApi.copied', 'Copié dans le presse-papier'), 'success', 2000);
  };

  // Get icon component
  const getIcon = (iconName: string) => {
    const IconComponent = Icons[iconName as keyof typeof Icons];
    return IconComponent ? <IconComponent size={18} /> : null;
  };

  // Tabs configuration
  const tabs: { id: TabType; label: string; icon: React.ReactNode }[] = [
    { id: 'overview', label: t('publicApi.tabs.overview', 'Vue d\'ensemble'), icon: <Icons.Home size={18} /> },
    { id: 'endpoints', label: t('publicApi.tabs.endpoints', 'Endpoints'), icon: <Icons.Code size={18} /> },
    { id: 'authentication', label: t('publicApi.tabs.auth', 'Authentification'), icon: <Icons.Lock size={18} /> },
    { id: 'examples', label: t('publicApi.tabs.examples', 'Exemples'), icon: <Icons.FileText size={18} /> },
    { id: 'keys', label: t('publicApi.tabs.keys', 'Mes Clés API'), icon: <Icons.Shield size={18} /> }
  ];

  return (
    <>
      {/* Mobile responsive CSS */}
      <style>{`
        @media (max-width: 640px) {
          .hide-mobile { display: none !important; }
          .hide-mobile-sm { display: none !important; }
          .show-mobile-only { display: block !important; }
        }
        @media (min-width: 641px) {
          .show-mobile-only { display: none !important; }
        }
        @media (max-width: 480px) {
          .hide-mobile-sm { display: none !important; }
        }
        @media (min-width: 481px) and (max-width: 640px) {
          .hide-mobile-sm { display: inline !important; }
        }
      `}</style>
      <div style={{ padding: 'clamp(12px, 4vw, 24px)', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 'clamp(16px, 4vw, 32px)' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'clamp(10px, 3vw, 16px)',
          marginBottom: '12px',
          flexWrap: 'wrap'
        }}>
          <div style={{
            width: 'clamp(44px, 10vw, 56px)',
            height: 'clamp(44px, 10vw, 56px)',
            borderRadius: '16px',
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <Icons.Code size={24} style={{ color: 'white' }} />
          </div>
          <div style={{ minWidth: 0, flex: '1 1 200px' }}>
            <h1 style={{ fontSize: 'clamp(20px, 5vw, 28px)', fontWeight: 700, margin: 0 }}>
              {t('publicApi.title', 'API Publique')}
            </h1>
            <p style={{ fontSize: 'clamp(12px, 3vw, 14px)', color: 'var(--muted)', margin: '4px 0 0' }}>
              {t('publicApi.subtitle', 'Documentation et gestion de l\'API REST')}
            </p>
          </div>
          <div style={{
            padding: '6px 12px',
            background: 'rgba(16, 185, 129, 0.1)',
            border: '1px solid rgba(16, 185, 129, 0.3)',
            borderRadius: '20px',
            fontSize: 'clamp(11px, 2.5vw, 13px)',
            fontWeight: 600,
            color: '#10b981',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            flexShrink: 0
          }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#10b981' }} />
            <span className="hide-mobile">{API_VERSION} - </span>{t('publicApi.status.operational', 'Operational')}
          </div>
        </div>

        {/* Base URL */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'clamp(8px, 2vw, 12px)',
          padding: 'clamp(10px, 2vw, 12px) clamp(12px, 3vw, 16px)',
          background: isDark ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.05)',
          borderRadius: '10px',
          fontFamily: 'monospace',
          fontSize: 'clamp(11px, 2.5vw, 14px)',
          flexWrap: 'wrap'
        }}>
          <span style={{ color: 'var(--muted)', flexShrink: 0 }}>Base URL:</span>
          <code style={{
            color: 'var(--accent)',
            fontWeight: 600,
            wordBreak: 'break-all',
            flex: '1 1 auto',
            minWidth: '150px'
          }}>{API_BASE_URL}</code>
          <button
            onClick={() => copyToClipboard(API_BASE_URL)}
            style={{
              padding: '6px 12px',
              background: 'var(--accent)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              flexShrink: 0
            }}
          >
            <Icons.Copy size={14} />
            <span className="hide-mobile-sm">{t('publicApi.copy', 'Copy')}</span>
          </button>
        </div>
      </div>

      {/* Tabs - Mobile optimized with horizontal scroll */}
      <div style={{
        display: 'flex',
        gap: '2px',
        marginBottom: 'clamp(16px, 4vw, 24px)',
        borderBottom: '1px solid var(--border)',
        paddingBottom: '0',
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none'
      }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: 'clamp(8px, 2vw, 12px) clamp(10px, 2.5vw, 20px)',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              border: 'none',
              background: activeTab === tab.id ? 'var(--accent)' : 'transparent',
              color: activeTab === tab.id ? 'white' : 'var(--muted)',
              borderRadius: '8px 8px 0 0',
              cursor: 'pointer',
              fontSize: 'clamp(12px, 2.5vw, 14px)',
              fontWeight: 500,
              transition: 'all 0.2s ease',
              whiteSpace: 'nowrap',
              flexShrink: 0
            }}
          >
            {tab.icon}
            <span className="hide-mobile-sm">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div style={{
        background: 'var(--card)',
        borderRadius: 'clamp(12px, 3vw, 16px)',
        padding: 'clamp(16px, 4vw, 24px)',
        minHeight: '400px'
      }}>
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div>
            <h2 style={{ fontSize: 'clamp(18px, 4vw, 20px)', fontWeight: 600, marginBottom: '16px' }}>
              {t('publicApi.overview.title', 'Bienvenue sur l\'API App.edouard.ai')}
            </h2>
            <p style={{ color: 'var(--muted)', lineHeight: 1.7, marginBottom: '24px', fontSize: 'clamp(13px, 3vw, 15px)' }}>
              {t('publicApi.overview.description', 'L\'API publique vous permet d\'accéder programmatiquement à vos données de portfolio, calculs fiscaux et monitoring de nodes. Intégrez facilement vos données dans vos outils préférés.')}
            </p>

            {/* Quick Stats - 2 columns on mobile, 4 on desktop */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 'clamp(8px, 2vw, 16px)',
              marginBottom: 'clamp(20px, 4vw, 32px)'
            }}>
              {[
                { label: t('publicApi.stats.endpoints', 'Endpoints'), value: '18', icon: 'Code', color: '#6366f1' },
                { label: t('publicApi.stats.uptime', 'Uptime'), value: '99.9%', icon: 'Activity', color: '#10b981' },
                { label: t('publicApi.stats.latency', 'Latency'), value: '<100ms', icon: 'Zap', color: '#f59e0b' },
                { label: t('publicApi.stats.version', 'Version'), value: 'v1.0.0', icon: 'Hash', color: '#8b5cf6' }
              ].map((stat, idx) => (
                <div key={idx} style={{
                  padding: 'clamp(12px, 3vw, 20px)',
                  background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                  borderRadius: '12px',
                  border: '1px solid var(--border)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(6px, 2vw, 10px)', marginBottom: '8px' }}>
                    <div style={{
                      width: 'clamp(28px, 6vw, 36px)',
                      height: 'clamp(28px, 6vw, 36px)',
                      borderRadius: '10px',
                      background: `${stat.color}20`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      {getIcon(stat.icon)}
                    </div>
                    <span style={{ fontSize: 'clamp(11px, 2.5vw, 13px)', color: 'var(--muted)' }}>{stat.label}</span>
                  </div>
                  <div style={{ fontSize: 'clamp(18px, 4vw, 24px)', fontWeight: 700, color: stat.color }}>
                    {stat.value}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Endpoints Tab */}
        {activeTab === 'endpoints' && (
          <div>
            <h2 style={{ fontSize: 'clamp(18px, 4vw, 20px)', fontWeight: 600, marginBottom: 'clamp(16px, 4vw, 24px)' }}>
              {t('publicApi.endpoints.title', 'Documentation des Endpoints')}
            </h2>

            {ENDPOINTS.map((category, catIdx) => (
              <div key={catIdx} style={{ marginBottom: 'clamp(20px, 4vw, 32px)' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  marginBottom: '16px'
                }}>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px',
                    background: 'var(--accent)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    {getIcon(category.icon)}
                  </div>
                  <h3 style={{ fontSize: 'clamp(14px, 3vw, 16px)', fontWeight: 600, margin: 0 }}>
                    {category.category}
                  </h3>
                </div>

                {category.endpoints.map((endpoint, endIdx) => {
                  const endpointKey = `${category.category}-${endpoint.path}`;
                  const isExpanded = expandedEndpoint === endpointKey;

                  return (
                    <div key={endIdx} style={{
                      background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                      borderRadius: '12px',
                      border: '1px solid var(--border)',
                      marginBottom: '12px',
                      overflow: 'hidden'
                    }}>
                      {/* Endpoint Header - Mobile optimized */}
                      <div
                        onClick={() => setExpandedEndpoint(isExpanded ? null : endpointKey)}
                        style={{
                          padding: 'clamp(12px, 3vw, 16px) clamp(12px, 3vw, 20px)',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 'clamp(8px, 2vw, 12px)',
                          flexWrap: 'wrap'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', flex: '1 1 auto', minWidth: 0 }}>
                          <span style={{
                            padding: '4px 8px',
                            background: endpoint.method === 'GET' ? '#10b98120' : '#f59e0b20',
                            color: endpoint.method === 'GET' ? '#10b981' : '#f59e0b',
                            borderRadius: '6px',
                            fontSize: 'clamp(10px, 2.5vw, 12px)',
                            fontWeight: 700,
                            fontFamily: 'monospace',
                            flexShrink: 0
                          }}>
                            {endpoint.method}
                          </span>
                          <code style={{ fontSize: 'clamp(12px, 3vw, 14px)', fontWeight: 600, wordBreak: 'break-all' }}>
                            {endpoint.path}
                          </code>
                          {!endpoint.auth && (
                            <span style={{
                              padding: '2px 6px',
                              background: 'rgba(107, 114, 128, 0.2)',
                              color: '#6b7280',
                              borderRadius: '4px',
                              fontSize: '10px',
                              fontWeight: 600,
                              flexShrink: 0
                            }}>
                              PUBLIC
                            </span>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto' }}>
                          <span className="hide-mobile" style={{ color: 'var(--muted)', fontSize: '13px', textAlign: 'right' }}>
                            {endpoint.description}
                          </span>
                          <Icons.ChevronDown
                            size={18}
                            style={{
                              color: 'var(--muted)',
                              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)',
                              transition: 'transform 0.2s',
                              flexShrink: 0
                            }}
                          />
                        </div>
                      </div>

                      {/* Expanded Content */}
                      {isExpanded && (
                        <div style={{
                          padding: '0 clamp(12px, 3vw, 20px) clamp(12px, 3vw, 20px)',
                          borderTop: '1px solid var(--border)'
                        }}>
                          {/* Show description on mobile when expanded */}
                          <p className="show-mobile-only" style={{
                            color: 'var(--muted)',
                            fontSize: '13px',
                            marginTop: '12px',
                            marginBottom: '0'
                          }}>
                            {endpoint.description}
                          </p>

                          {endpoint.auth && (
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: 'clamp(8px, 2vw, 12px)',
                              background: 'rgba(139, 92, 246, 0.1)',
                              borderRadius: '8px',
                              marginTop: '12px',
                              fontSize: 'clamp(11px, 2.5vw, 13px)',
                              flexWrap: 'wrap'
                            }}>
                              <Icons.Lock size={16} style={{ color: '#8b5cf6', flexShrink: 0 }} />
                              <span>{t('publicApi.authRequired', 'Auth required')}</span>
                              {endpoint.feature && (
                                <span style={{
                                  marginLeft: 'auto',
                                  padding: '2px 8px',
                                  background: '#8b5cf620',
                                  color: '#8b5cf6',
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                  fontWeight: 600
                                }}>
                                  {endpoint.feature}
                                </span>
                              )}
                            </div>
                          )}

                          {endpoint.params.length > 0 && (
                            <>
                              <h4 style={{ fontSize: 'clamp(13px, 3vw, 14px)', fontWeight: 600, marginTop: '16px', marginBottom: '12px' }}>
                                {t('publicApi.parameters', 'Parameters')}
                              </h4>
                              <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', margin: '0 -12px', padding: '0 12px' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'clamp(11px, 2.5vw, 13px)', minWidth: '400px' }}>
                                  <thead>
                                    <tr style={{ borderBottom: '1px solid var(--border)' }}>
                                      <th style={{ textAlign: 'left', padding: '8px 8px 8px 0', color: 'var(--muted)', whiteSpace: 'nowrap' }}>{t('publicApi.table.name', 'Name')}</th>
                                      <th style={{ textAlign: 'left', padding: '8px', color: 'var(--muted)', whiteSpace: 'nowrap' }}>{t('publicApi.table.type', 'Type')}</th>
                                      <th style={{ textAlign: 'left', padding: '8px', color: 'var(--muted)', whiteSpace: 'nowrap' }}>{t('publicApi.table.default', 'Default')}</th>
                                      <th style={{ textAlign: 'left', padding: '8px 0 8px 8px', color: 'var(--muted)' }}>{t('publicApi.table.description', 'Description')}</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {endpoint.params.map((param, pIdx) => (
                                      <tr key={pIdx} style={{ borderBottom: '1px solid var(--border)' }}>
                                        <td style={{ padding: '10px 8px 10px 0', whiteSpace: 'nowrap' }}>
                                          <code style={{ color: 'var(--accent)' }}>{param.name}</code>
                                          {param.required && (
                                            <span style={{ color: '#ef4444', marginLeft: '4px' }}>*</span>
                                          )}
                                        </td>
                                        <td style={{ padding: '10px 8px', color: 'var(--muted)', whiteSpace: 'nowrap' }}>{param.type}</td>
                                        <td style={{ padding: '10px 8px', fontFamily: 'monospace', fontSize: 'clamp(10px, 2vw, 12px)', whiteSpace: 'nowrap' }}>
                                          {param.default || '-'}
                                        </td>
                                        <td style={{ padding: '10px 0 10px 8px' }}>{param.description}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </>
                          )}

                          {/* Example Request */}
                          <h4 style={{ fontSize: '14px', fontWeight: 600, marginTop: '20px', marginBottom: '12px' }}>
                            {t('publicApi.example', 'Example')}
                          </h4>
                          <div style={{
                            background: isDark ? '#1a1a2e' : '#f8fafc',
                            borderRadius: '8px',
                            padding: '16px',
                            fontFamily: 'monospace',
                            fontSize: '13px',
                            overflow: 'auto'
                          }}>
                            <code>
                              curl -H "Authorization: Bearer sk_live_xxx" \<br />
                              &nbsp;&nbsp;"{API_BASE_URL}{endpoint.path}"
                            </code>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}

        {/* Authentication Tab */}
        {activeTab === 'authentication' && (
          <div>
            <h2 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '16px' }}>
              {t('publicApi.auth.title', 'Authentication')}
            </h2>
            <p style={{ color: 'var(--muted)', lineHeight: 1.7, marginBottom: '24px' }}>
              {t('publicApi.auth.description', 'All requests (except /status) require a valid API key.')}
            </p>

            {/* Step by step */}
            <div style={{ marginBottom: '32px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>
                {t('publicApi.auth.getKeyTitle', 'Get an API key')}
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {[
                  { step: 1, text: t('publicApi.auth.step1', 'Log in to app.edouard.ai') },
                  { step: 2, text: t('publicApi.auth.step2', 'Go to Settings > API') },
                  { step: 3, text: t('publicApi.auth.step3', 'Click "Generate an API key"') },
                  { step: 4, text: t('publicApi.auth.step4', 'Copy and securely store your key') }
                ].map((item, idx) => (
                  <div key={idx} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    padding: '16px',
                    background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                    borderRadius: '10px'
                  }}>
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      background: 'var(--accent)',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      fontSize: '14px'
                    }}>
                      {item.step}
                    </div>
                    <span>{item.text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Usage */}
            <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>
              {t('publicApi.auth.useKeyTitle', 'Use the API key')}
            </h3>
            <p style={{ color: 'var(--muted)', marginBottom: '16px', fontSize: '14px' }}>
              {t('publicApi.auth.useKeyDesc', 'Include your key in the Authorization or X-API-Key header:')}
            </p>

            <div style={{
              background: isDark ? '#1a1a2e' : '#f8fafc',
              borderRadius: '10px',
              padding: '20px',
              fontFamily: 'monospace',
              fontSize: '13px',
              marginBottom: '24px'
            }}>
              <div style={{ marginBottom: '16px' }}>
                <span style={{ color: 'var(--muted)' }}># {t('publicApi.auth.option1', 'Option 1: Authorization header')}</span><br />
                <code>Authorization: Bearer sk_live_xxxxx</code>
              </div>
              <div>
                <span style={{ color: 'var(--muted)' }}># {t('publicApi.auth.option2', 'Option 2: X-API-Key header')}</span><br />
                <code>X-API-Key: sk_live_xxxxx</code>
              </div>
            </div>

            {/* Rate Limit Headers */}
            <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>
              {t('publicApi.auth.rateLimitTitle', 'Rate Limit Headers')}
            </h3>
            <p style={{ color: 'var(--muted)', marginBottom: '16px', fontSize: '14px' }}>
              {t('publicApi.auth.rateLimitDesc', 'Each response includes rate limiting headers:')}
            </p>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <tbody>
                {[
                  { header: 'X-RateLimit-Limit', description: t('publicApi.auth.rateLimitTotal', 'Total daily limit') },
                  { header: 'X-RateLimit-Remaining', description: t('publicApi.auth.rateLimitRemaining', 'Remaining requests') },
                  { header: 'X-RateLimit-Reset', description: t('publicApi.auth.rateLimitReset', 'Unix timestamp for reset') }
                ].map((item, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '12px 0' }}>
                      <code style={{ color: 'var(--accent)' }}>{item.header}</code>
                    </td>
                    <td style={{ padding: '12px 0', color: 'var(--muted)' }}>{item.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Examples Tab */}
        {activeTab === 'examples' && (
          <div>
            <h2 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '16px' }}>
              {t('publicApi.examples.title', 'Exemples de Code')}
            </h2>

            {/* Language selector */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
              {(['curl', 'javascript', 'python'] as CodeLang[]).map(lang => (
                <button
                  key={lang}
                  onClick={() => setSelectedLang(lang)}
                  style={{
                    padding: '8px 16px',
                    background: selectedLang === lang ? 'var(--accent)' : 'transparent',
                    color: selectedLang === lang ? 'white' : 'var(--muted)',
                    border: '1px solid var(--border)',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: 500
                  }}
                >
                  {lang === 'curl' ? 'cURL' : lang === 'javascript' ? 'JavaScript' : 'Python'}
                </button>
              ))}
            </div>

            {/* Code block */}
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => copyToClipboard(CODE_EXAMPLES[selectedLang])}
                style={{
                  position: 'absolute',
                  top: '12px',
                  right: '12px',
                  padding: '6px 12px',
                  background: 'rgba(255,255,255,0.1)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '12px'
                }}
              >
                <Icons.Copy size={14} />
                {t('publicApi.copy', 'Copy')}
              </button>
              <pre style={{
                background: isDark ? '#1a1a2e' : '#1e293b',
                color: '#e2e8f0',
                borderRadius: '12px',
                padding: '24px',
                overflow: 'auto',
                fontSize: '13px',
                lineHeight: 1.6
              }}>
                <code>{CODE_EXAMPLES[selectedLang]}</code>
              </pre>
            </div>
          </div>
        )}

        {/* API Keys Tab */}
        {activeTab === 'keys' && (
          <div>
            <h2 style={{ fontSize: 'clamp(18px, 4vw, 20px)', fontWeight: 600, marginBottom: '16px' }}>
              {t('publicApi.keys.title', 'Mes Clés API')}
            </h2>

            {/* Redirect to Settings */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 'clamp(30px, 8vw, 60px) 20px',
              textAlign: 'center'
            }}>
              <div style={{
                width: 'clamp(64px, 15vw, 80px)',
                height: 'clamp(64px, 15vw, 80px)',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(99, 102, 241, 0.2) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px'
              }}>
                <Icons.Shield size={32} style={{ color: 'var(--accent)' }} />
              </div>
              <h3 style={{ fontSize: 'clamp(16px, 3.5vw, 18px)', fontWeight: 600, marginBottom: '8px' }}>
                {t('publicApi.keys.manageTitle', 'Gérer vos clés API')}
              </h3>
              <p style={{ color: 'var(--muted)', maxWidth: '450px', lineHeight: 1.6, marginBottom: '24px', fontSize: 'clamp(13px, 3vw, 14px)' }}>
                {t('publicApi.keys.manageDesc', 'Générez, régénérez ou révoquez vos clés API depuis la page Paramètres. Vos clés sont stockées localement de manière sécurisée.')}
              </p>

              <button
                onClick={onNavigateToSettings}
                style={{
                  padding: 'clamp(10px, 2.5vw, 14px) clamp(20px, 5vw, 32px)',
                  background: 'var(--accent)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: 'clamp(13px, 3vw, 15px)',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  (e.target as HTMLButtonElement).style.transform = 'translateY(-2px)';
                  (e.target as HTMLButtonElement).style.boxShadow = '0 6px 20px rgba(139, 92, 246, 0.35)';
                }}
                onMouseLeave={(e) => {
                  (e.target as HTMLButtonElement).style.transform = 'translateY(0)';
                  (e.target as HTMLButtonElement).style.boxShadow = 'none';
                }}
              >
                <Icons.Settings size={18} />
                {t('publicApi.keys.goToSettings', 'Aller aux Paramètres')}
                <Icons.ChevronRight size={18} />
              </button>

              {/* Info box */}
              <div style={{
                marginTop: '32px',
                padding: 'clamp(12px, 3vw, 16px)',
                background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                borderRadius: '12px',
                border: '1px solid var(--border)',
                maxWidth: '500px',
                width: '100%'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px',
                  textAlign: 'left'
                }}>
                  <Icons.Info size={20} style={{ color: 'var(--accent)', flexShrink: 0, marginTop: '2px' }} />
                  <div>
                    <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '6px' }}>
                      {t('publicApi.keys.securityNote', 'Note de sécurité')}
                    </h4>
                    <p style={{ fontSize: '13px', color: 'var(--muted)', lineHeight: 1.5 }}>
                      {t('publicApi.keys.securityDesc', 'Votre clé API est stockée uniquement sur votre appareil. Ne la partagez jamais et gardez-la en sécurité. Vous pouvez la régénérer à tout moment depuis les paramètres.')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{
        marginTop: 'clamp(20px, 4vw, 32px)',
        padding: 'clamp(12px, 3vw, 20px)',
        background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
        borderRadius: '12px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        <div style={{ fontSize: 'clamp(11px, 2.5vw, 13px)', color: 'var(--muted)' }}>
          {t('publicApi.lastUpdated', 'Last updated')}: {t('publicApi.updateDate', 'January 2026')}
        </div>
        <a href="mailto:api@edouard.ai" style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          color: 'var(--muted)',
          textDecoration: 'none',
          fontSize: 'clamp(11px, 2.5vw, 13px)'
        }}>
          <Icons.Send size={16} />
          {t('publicApi.support', 'Support')}
        </a>
      </div>
      </div>
    </>
  );
}

export default PublicAPI;
